import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { BrandHeader } from './BrandHeader';

export type AppShellMode = 'kid' | 'parent';

export interface AppShellProps {
  children: ReactNode;
  /** Kid mode uses warm cream/orange backgrounds; parent mode is cool white/slate. */
  mode?: AppShellMode;
  /** When true, hides the BrandHeader — used by Login/Onboarding which have their own chrome. */
  bare?: boolean;
  className?: string;
}

/**
 * AppShell — the standard page wrapper from docs/DESIGN.md. Sets the
 * `data-mode` attribute so global CSS selectors (`[data-mode="parent"]`)
 * can swap the page background without every page needing its own class.
 *
 * Accessibility:
 *   - Ships a "Skip to main content" link that is positioned off-screen
 *     until focused, then anchors into view at the top-left. Most
 *     authenticated pages have a dense BrandHeader + nav, so keyboard
 *     users need a way to bypass the repeated landmarks on every page.
 *   - `<main id="main-content" tabIndex={-1}>` exposes the landmark role
 *     for assistive tech and makes the skip-link target focusable without
 *     adding it to the regular tab order.
 */
export function AppShell({ children, mode = 'kid', bare = false, className }: AppShellProps) {
  return (
    <div data-mode={mode} className={cn('min-h-screen', mode === 'parent' && 'bg-white')}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-or-orange-500 focus:px-4 focus:py-2 focus:text-[13px] focus:font-semibold focus:text-white focus:shadow-or-btn"
      >
        Skip to main content
      </a>
      {!bare && <BrandHeader />}
      <main
        id="main-content"
        tabIndex={-1}
        className={cn('mx-auto w-full max-w-[1200px] px-6 py-8 focus:outline-none', className)}
      >
        {children}
      </main>
    </div>
  );
}
